import {call, put, takeEvery, select} from 'redux-saga/effects';
import * as actionTypes from './actionTypes';
import axios from 'axios';
import {I18n} from 'react-redux-i18n';
import moment from 'moment';
import { editEntityCall } from '../../baseApp/entities/sagas';
import { getUserPageRefresh } from '../../baseApp/auth/sagas';

const controllerUrl = '/parking/';

const fetchParkingList = () => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'list').then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const fetchParkingOccupancy = parkingId => new Promise((resolve, reject) => {
    axios.get('/parking/' + parkingId + '/occupancy/').then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const fetchParkingTableData = (parkingId, searchFields) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'slots/search', {
        params: {
            ...searchFields,
            parkingFloorId: searchFields ? searchFields.parkingFloor : null,
            parkingId
        }
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const fetchParkingGraphData = (parkingId, searchFields) => new Promise((resolve, reject) => {
    axios.get(controllerUrl + parkingId + '/history', {
        params: {
            ...searchFields,
            // floorId: searchFields ? searchFields.parkingFloor : null
        }
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const fetchFloorList = parkingId => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'floors/list', {
        params: {
            parkingId
        }
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const fetchCategoryList = () => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'categories').then(
        response => resolve(response.data),
        error => reject(error)
    );
});

const fetchStatusList = () => new Promise((resolve, reject) => {
    resolve([{
        id: "FREE",
        name: I18n.t('parking.status.free')
    }, {
        id: "BUSY",
        name: I18n.t('parking.status.occupied')
    }])
});

const fetchSlotList = parkingId => new Promise((resolve, reject) => {
    axios.get(controllerUrl + 'slots/list', {
        params: {
            parkingId
        }
    }).then(
        response => resolve(response.data),
        error => reject(error)
    );
});

function* fetchParkingListSaga() {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const parkingList = yield call(fetchParkingList);

        yield put({
            type: actionTypes.GET_PARKING_LIST_SUCCESS,
            payload: parkingList
        });
    } catch (e) {
        yield put({
            type: actionTypes.GET_PARKING_LIST_FAILED,
            payload: e
        });
    }
}

function* editSlotCategory({ slotId, category, selectedSlotName,slotsData }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        slotsData.category = category.label;
        slotsData.status = slotsData.status.toUpperCase();

        const editedSlot = yield call(editEntityCall, slotId, slotsData);

        yield put({
            type: actionTypes.CHANGE_SPOT_CATEGORY_SUCCESS,
            payload: {
                editedSlot
            }
        });
    } catch (e) {
        yield put({
            type: actionTypes.CHANGE_SPOT_CATEGORY_FAILED,
            payload: I18n.t('parking.page.categoryChange.error')
        });
    }
}

function* fetchParkingSlotsDataSaga({ parkingId }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const parkingOccupancy = yield call(fetchParkingOccupancy, parkingId);
        const {
            freeSlots,
            busySlots,
            categoriesSlotsOccupancy
        } = parkingOccupancy;

        yield put({
            type: actionTypes.LOAD_PARKING_DATA_SUCCESS,
            payload: {
                totalSlotsData: {
                    freeSlots: freeSlots,
                    occupiedSlots: busySlots,
                    parkingSlots: freeSlots + busySlots
                },
                categoriesData: categoriesSlotsOccupancy.map(t => {
                    return {
                        id: t.category,
                        name: t.category,
                        freeSlots: t.freeSlots,
                        occupiedSlots: t.busySlots,
                        parkingSlots: t.freeSlots + t.busySlots
                    }
                })
            }
        });
    } catch (e) {
        yield put({
            type: actionTypes.LOAD_PARKING_DATA_FAILED,
            payload: e
        });
    }
}

function* fetchParkingTableDataSaga({ parkingId, searchFields = { } }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        searchFields.itemsPerPage = searchFields.pageSize;
        if(searchFields.sortDirection) {
            searchFields.sortDirection = searchFields.sortDirection.toUpperCase();
        }

        const searchResults = yield call(fetchParkingTableData, parkingId, searchFields);
        const sort = {
            direction: searchResults.sort[0].direction,
            property: searchResults.sort[0].property
        };

        if(searchResults.sort[0].property === 'parent.name') {
            sort.property = 'id';
        }

        const searchResultsWithFloors = {
            ...searchResults,
            sort,
            pageNumber: searchResults.number,
            content: searchResults.content.map(
                slot => ({
                    ...slot,
                    status: slot.status ? I18n.t('parking.status.' + slot.status.toLowerCase()) : "- - -",
                    category: {
                        id: slot.category? slot.category : 'noCategory',
                        name: slot.category? slot.category : I18n.t('parking.categories.noCategory'),
                    }
                })
            )
        };

        yield put({
            type: actionTypes.LOAD_PARKING_SPOTS_TABLE_DATA_SUCCESS,
            payload: searchResultsWithFloors
        });
    } catch (e) {
        console.error(e);
        yield put({
            type: actionTypes.LOAD_PARKING_SPOTS_TABLE_DATA_FAILED,
            payload: e
        });
    }
}

function* fetchParkingGraphDataSaga({ parkingId, searchFields = {} }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        delete searchFields.slotId;
        delete searchFields.floorId;
        delete searchFields.category;


        // if(!searchFields.startTime && !searchFields.endTime) {
        //     //Shows last day, if nothing was selected
        //     searchFields.startTime = moment().subtract(1, 'd');
        //     searchFields.endTime =  moment();
        // }
        // if(!searchFields.startDate|| !searchFields.endDate) {
        if(searchFields.endDate && searchFields.endDate.value !== "") {
            //End date = today
            searchFields.endTime = moment(searchFields.endDate.value);
        } else {
            searchFields.endTime = moment();
        }
        if(searchFields.startDate && searchFields.startDate.value !== "") {
            //Start date = day before end date
            searchFields.startTime = moment(searchFields.startDate.value);
        } else {
            searchFields.startTime = moment(searchFields.endTime);
            searchFields.startTime.subtract(1, "d");
        }
        // }

        searchFields.startWorkingHours = (searchFields.startWorkingHours && searchFields.startWorkingHours.value !== "") ? searchFields.startWorkingHours.value : '08:00:00';
        searchFields.endWorkingHours = (searchFields.endWorkingHours && searchFields.endWorkingHours.value !== "") ? searchFields.endWorkingHours.value : '20:00:00';

        const diffDays = searchFields.endTime.diff(searchFields.startTime, 'days');

        if(diffDays > 7) {
            searchFields.step = 86400;
        } else if( diffDays > 2) {
            searchFields.step = 3600;
        } else {
            searchFields.step = 900;
        }

        //Convert this bastards only after the diff was used
        if(searchFields.startDate && searchFields.startDate.value !== ""){
            searchFields.startDate = searchFields.startDate.value;
        } else {
            searchFields.startDate = searchFields.startTime.format('YYYY-MM-DD');
        }

        if(searchFields.endDate && searchFields.endDate.value !== ""){
            searchFields.endDate = searchFields.endDate.value;
        } else {
            searchFields.endDate = searchFields.endTime.format('YYYY-MM-DD');
        }

        delete searchFields.startTime;
        delete searchFields.endTime;

        if(searchFields.slot?.value !== ""){
            searchFields.slotId = searchFields.slot?.value;
        }

        delete searchFields.slot;


        if(searchFields.parkingFloor?.value !== ""){
            searchFields.floorId = searchFields.parkingFloor?.value;
        }

        delete searchFields.parkingFloor;

        if(searchFields.category?.value !== ""){
            searchFields.category = searchFields.category?.value;
        }



        const graphData = yield call(fetchParkingGraphData, parkingId, searchFields);


        let tailoredGraphData = [{
            name: I18n.t('parking.status.occupied'),
            type: "line",
            color: '#d0021b',
            tooltip: {
                pointFormat: "<b>" + I18n.t('parking.status.occupied') + ":</b> {point.busy} slots <br>"
            },
            data: graphData
                .map(e => ({
                    x: e.timestamp,
                    y: ((e.busy / (e.busy + e.free)) * 100),
                    busy: e.busy
                }))
        }, {
            name: I18n.t('parking.status.free'),
            type: "line",
            color: '#7ed321',
            tooltip: {
                pointFormat: "<b>" + I18n.t('parking.status.free') + ":</b> {point.free} slots <br>"
            },
            visible: false,
            data: graphData
                .map(e => ({
                    x: e.timestamp,
                    y: ((e.free / (e.busy + e.free)) * 100),
                    free: e.free
                }))
        }];

        const occupationAverage = tailoredGraphData[0].data.reduce((acc, p) => acc + p.y, 0) / tailoredGraphData[0].data.length;

        tailoredGraphData.push({
            name: I18n.t('parking.status.occupiedAvg'),
            type: "line",
            color: '#d0021b',
            dashStyle: 'Dash',
            opacity: 0.5,
            marker: {
                enabled: false
            },
            tooltip: {
                pointFormat: "<b>" + I18n.t('parking.status.occupiedAvg') + ":</b> {point.y}% <br>"
            },
            data: tailoredGraphData[0].data
                .map(e => ({
                    x: e.x,
                    y: parseFloat(occupationAverage.toFixed(2))
                }))
        });

        yield put({
            type: actionTypes.LOAD_PARKING_SPOTS_GRAPH_DATA_SUCCESS,
            payload: tailoredGraphData
        });
    } catch (e) {
        yield put({
            type: actionTypes.LOAD_PARKING_SPOTS_GRAPH_DATA_FAILED,
            payload: e
        });
    }
}

function* fetchSearchFieldsDataSaga({ parkingId }) {
    try {
        const userDefined = yield select(state => state.authReducer.user);

        if (!userDefined || !userDefined.role) {
            yield call(getUserPageRefresh);
        }
        const floorData = yield call(fetchFloorList, parkingId);
        const categoryData = yield call(fetchParkingOccupancy, parkingId);
        const categoriesAllData = yield call(fetchCategoryList, parkingId);
        const statusData = yield call(fetchStatusList);
        const slotData = yield call(fetchSlotList, parkingId);

        yield put({
            type: actionTypes.LOAD_SEARCH_FIELDS_DATA_SUCCESS,
            payload: {
                categoryData: categoryData.categoriesSlotsOccupancy.map(c => {
                    return {
                        id: c.category,
                        name: c.category,
                    }}),
                categoriesAllData: categoriesAllData.map(t => {
                    return {
                        id: t,
                        name: t
                    }}),
                floorData,
                statusData,
                slotData
            }
        });
    } catch (e) {
        yield put({
            type: actionTypes.LOAD_SEARCH_FIELDS_DATA_FAILED,
            payload: e
        });
    }
}

const parkingSagas = [
    takeEvery(actionTypes.GET_PARKING_LIST_REQUEST, fetchParkingListSaga),
    takeEvery(actionTypes.LOAD_PARKING_DATA_REQUEST, fetchParkingSlotsDataSaga),
    takeEvery(actionTypes.LOAD_PARKING_SPOTS_TABLE_DATA_REQUEST, fetchParkingTableDataSaga),
    takeEvery(actionTypes.LOAD_PARKING_SPOTS_GRAPH_DATA_REQUEST, fetchParkingGraphDataSaga),
    takeEvery(actionTypes.LOAD_SEARCH_FIELDS_DATA_REQUEST, fetchSearchFieldsDataSaga),
    takeEvery(actionTypes.CHANGE_SPOT_CATEGORY_REQUEST, editSlotCategory),
];

export {
    parkingSagas,
    fetchStatusList
};